// Shared bits for the slice 2 set-up screens.

import type { ComponentChildren } from 'preact';
import { Link } from 'wouter-preact';
import { TeachingPage, AssessmentSwitcher, INK } from './ui';
import { assessments, teachHref } from '@/lib/teaching/state';
import { FINALISED_TEXT } from '@/lib/teaching/slice4';
import { aiBlock } from '@/lib/teaching/ai-ready';

/** "TEACHER CONFIRMED" reads as shouting: show "Teacher confirmed".
 *  Acronyms (AI, MYP, ID) and codes with digits keep their case. */
export function sentenceCase(label: string): string {
  if (label !== label.toUpperCase() || !/[A-Z]{3}/.test(label)) return label;
  const out = label.split(/(\s+)/).map((w) => (/^(AI|MYP|ID|PDF|QC)[,:.]?$/.test(w) || /\d/.test(w) ? w : w.toLowerCase())).join('');
  return out.charAt(0).toUpperCase() + out.slice(1);
}

export function Pill({ label, bg, title }: { label: string; bg: string; title?: string }) {
  return (
    <span title={title} class="inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] font-semibold tracking-[0.02em] whitespace-nowrap" style={{ background: bg, color: INK }}>
      {sentenceCase(label)}
    </span>
  );
}

export function Tabs<T extends string>({ value, options, onChange, label }: { value: T; options: { id: T; label: string }[]; onChange: (v: T) => void; label: string }) {
  return (
    <div role="tablist" aria-label={label} class="inline-flex flex-wrap rounded-lg border border-[var(--color-border)] p-0.5 bg-[var(--color-card)] max-w-full self-start">
      {options.map((o) => (
        <button key={o.id} type="button" role="tab" aria-selected={value === o.id} onClick={() => onChange(o.id)}
          class={'px-3 py-1 rounded-md text-[12.5px] font-medium ' + (value === o.id ? 'bg-[var(--color-accent-soft)] font-semibold' : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]')}
          style={value === o.id ? { color: 'var(--color-accent)' } : undefined}>{o.label}</button>
      ))}
    </div>
  );
}

/** Page shell for screens that need a test (?a=). */
export function SetupPage({ a, title, sub, actions, children }: { a: string | null | undefined; title: string; sub?: string; actions?: ComponentChildren; children: ComponentChildren }) {
  const t = a ? assessments.value?.find((x) => x.id === a) : null;
  const crumb = sub ?? (t ? `${t.title} · Year ${t.year_level}` : '');
  return (
    <TeachingPage title={title} breadcrumb={crumb} actions={<>{a && <AssessmentSwitcher current={a} />}{actions}</>}>
      {a === null && <div class="text-[13px] text-[var(--color-text-muted)]">Create a test first. <Link href="/setup/tests/new" class="text-[var(--color-accent)]">New test</Link></div>}
      {a === undefined && <div class="text-[13px] text-[var(--color-text-muted)]">Loading…</div>}
      {a && t?.status === 'finalised' && (
        <FinalisedNote a={a} />
      )}
      {a && (t?.status === 'finalised'
        // A disabled fieldset disables every write control inside it (§06).
        ? <fieldset disabled title={FINALISED_TEXT} class="contents min-w-0 border-0 p-0 m-0" data-finalised-lock>{children}</fieldset>
        : children)}
    </TeachingPage>
  );
}

export const textareaCls = 'rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-2 text-[13px] text-[var(--color-text)] min-w-0 w-full';
export const selectCls = 'h-9 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-2 text-[13px] text-[var(--color-text)] min-w-0 max-w-full';

export function Note({ children, tone = 'info' }: { children: ComponentChildren; tone?: 'info' | 'warn' | 'ok' | 'bad' }) {
  const bg = { info: '#e8effb', warn: '#fff6e0', ok: '#e3f5e7', bad: '#fde2e2' }[tone];
  return <div class="rounded-xl px-3 py-2 text-[12.5px]" style={{ background: bg, color: INK }}>{children}</div>;
}

/** Shown on every per-test screen while the test is finalised. */
export function FinalisedNote({ a }: { a: string }) {
  return (
    <div class="rounded-xl px-3 py-2 text-[12.5px] flex flex-wrap items-center gap-2" style={{ background: '#d3f0d9', color: INK }} data-finalised-note>
      <b>{FINALISED_TEXT}.</b> <span>This test is finalised, so nothing on it can be changed.</span>
      <Link href={teachHref('/teaching/checkpoints', { a })} class="underline" style={{ color: INK }}>Checkpoints and reopen</Link>
    </div>
  );
}

/** Shown beside an AI button when no provider is connected. */
export function NoAiNote() {
  return (
    <span class="text-[12.5px] text-[var(--color-text-muted)]" data-no-ai={aiBlock.value ?? 'no_key'}>
      {aiBlock.value === 'not_chosen' ? 'Choose an AI provider in ' : 'Add an AI key in '}<Link href="/setup/settings" class="text-[var(--color-accent)] underline">Settings</Link> to use this
    </span>
  );
}
